import React, { useState, useRef, useEffect } from 'react';
import { FiVolume2, FiAlertTriangle } from 'react-icons/fi';
import SystemPlaybackSpeedControl, { applySystemPlaybackRate, useSystemPlaybackRate } from './SystemPlaybackSpeedControl';
import './AudioRecorder.css';

interface AudioRecorderProps {
  onRecordingComplete?: (audioBlob: Blob, duration?: number) => void;
  systemAudioURL?: string;
  onSystemAudioComplete?: () => void;
  onFinishAttempt?: () => void;
  onPlaybackComplete?: () => void;
  onStartRecording?: () => void;
  onRecordingStreamReady?: (stream: MediaStream) => void; // Called when mic stream is ready (for real-time caption)
  onRecordingStopped?: () => void;
  onRepeatSystemAudio?: () => void;
  onStart?: () => void; // Callback when Start button is clicked
  onPrevious?: () => void;
  showPrevious?: boolean;
  canGoPrevious?: boolean;
  isRecording?: boolean;
  autoPlay?: boolean;
  autoPlayRecording?: boolean;
  disabled?: boolean;
  attempts?: number; // Number of attempts for current audio
  /** While true, opens the mic early (e.g. during autoplay chime) so recording can start immediately after. */
  prewarmMic?: boolean;
  /** Increment to drop a prewarmed mic stream when autoplay is cancelled (not recording). */
  micPrewarmRevokeTick?: number;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ 
  onRecordingComplete, 
  systemAudioURL,
  onSystemAudioComplete,
  onFinishAttempt,
  onPlaybackComplete,
  onStartRecording,
  onRecordingStreamReady,
  onRecordingStopped,
  onRepeatSystemAudio,
  onStart,
  onPrevious,
  showPrevious = false,
  canGoPrevious = false,
  isRecording: externalIsRecording,
  autoPlay = false,
  autoPlayRecording = false,
  disabled = false,
  attempts: externalAttempts = 0,
  prewarmMic = false,
  micPrewarmRevokeTick = 0
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSystemAudioPlaying, setIsSystemAudioPlaying] = useState(false);
  const [systemAudioFinished, setSystemAudioFinished] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [systemAudioDuration, setSystemAudioDuration] = useState(0);
  const [systemAudioCurrentTime, setSystemAudioCurrentTime] = useState(0);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [systemWaveformData, setSystemWaveformData] = useState<number[]>([]);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [systemPlaybackRate, setSystemPlaybackRate] = useSystemPlaybackRate();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const systemAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const systemAnalyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const micMediaStreamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const isRecordingRef = useRef<boolean>(false);
  const audioURLRef = useRef<string | null>(null); // Ref to track current audio URL for cleanup

  useEffect(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    return () => {
      if (micMediaStreamSourceRef.current) {
        try {
          micMediaStreamSourceRef.current.disconnect();
        } catch {
          /* ignore */
        }
        micMediaStreamSourceRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Reset systemAudioFinished when systemAudioURL changes
  useEffect(() => {
    setSystemAudioFinished(false);
  }, [systemAudioURL]);

  useEffect(() => {
    applySystemPlaybackRate(systemAudioRef.current, systemPlaybackRate);
  }, [systemPlaybackRate, systemAudioURL]);

  // Handle system audio
  useEffect(() => {
    const systemAudio = systemAudioRef.current;
    if (!systemAudio || !systemAudioURL) {
      setSystemAudioFinished(false);
      return;
    }

    // Set volume to maximum when audio element is available
    systemAudio.volume = 1.0;

    const handleLoadedMetadata = () => {
      setSystemAudioDuration(systemAudio.duration);
      generateStaticWaveform(systemAudio.duration, setSystemWaveformData);
      systemAudio.volume = 1.0;
      applySystemPlaybackRate(systemAudio, systemPlaybackRate);
    };

    const handleTimeUpdate = () => {
      setSystemAudioCurrentTime(systemAudio.currentTime);
    };

    const handleEnded = () => {
      setIsSystemAudioPlaying(false);
      setSystemAudioCurrentTime(0);
      setSystemAudioFinished(true);
      // Don't automatically start recording - user must click a button to start
      if (onSystemAudioComplete) {
        onSystemAudioComplete();
      }
    };

    systemAudio.addEventListener('loadedmetadata', handleLoadedMetadata);
    systemAudio.addEventListener('timeupdate', handleTimeUpdate);
    systemAudio.addEventListener('ended', handleEnded);

    return () => {
      systemAudio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      systemAudio.removeEventListener('timeupdate', handleTimeUpdate);
      systemAudio.removeEventListener('ended', handleEnded);
    };
  }, [systemAudioURL, isRecording, onSystemAudioComplete, disabled, systemPlaybackRate]);

  // Handle autoPlay separately
  useEffect(() => {
    console.log('[AudioRecorder] autoPlay useEffect triggered:', {
      autoPlay,
      disabled,
      hasSystemAudioURL: !!systemAudioURL,
      systemAudioURL: systemAudioURL ? systemAudioURL.substring(0, 50) + '...' : null
    });

    if (!autoPlay || disabled || !systemAudioURL) {
      console.log('[AudioRecorder] autoPlay conditions not met, returning early');
      return;
    }

    // Use a small delay to ensure audio element is mounted
    const timeoutId = setTimeout(() => {
      const systemAudio = systemAudioRef.current;
      if (!systemAudio) {
        console.warn('[AudioRecorder] Audio element not found when trying to auto-play');
        return;
      }

      console.log('[AudioRecorder] Audio element found, readyState:', systemAudio.readyState, {
        HAVE_NOTHING: 0,
        HAVE_METADATA: 1,
        HAVE_CURRENT_DATA: 2,
        HAVE_FUTURE_DATA: 3,
        HAVE_ENOUGH_DATA: 4
      }[systemAudio.readyState] || systemAudio.readyState);

      // Check if audio is ready to play
      const playAudio = () => {
        if (systemAudio.readyState >= 2) { // HAVE_CURRENT_DATA or higher
          console.log('[AudioRecorder] Audio is ready, attempting to play...');
          applySystemPlaybackRate(systemAudio, systemPlaybackRate);
          systemAudio.play()
            .then(() => {
              console.log('[AudioRecorder] Audio play() succeeded');
              setIsSystemAudioPlaying(true);
              // Setup visualization if not already done
              if (audioContextRef.current && systemAudio) {
                try {
                  // Check if source already exists to avoid error
                  if (!systemAnalyserRef.current) {
                    console.log('[AudioRecorder] Setting up audio visualization');
                    const source = audioContextRef.current.createMediaElementSource(systemAudio);
                    const analyser = audioContextRef.current.createAnalyser();
                    analyser.fftSize = 256;
                    analyser.smoothingTimeConstant = 0.8;
                    systemAnalyserRef.current = analyser;
                    // Connect source -> analyser -> destination (important: must connect to destination!)
                    source.connect(analyser);
                    analyser.connect(audioContextRef.current.destination);
                  }
                  audioContextRef.current.resume();
                  visualizeSystemWaveform();
                } catch (error) {
                  // Source might already exist, that's okay
                  if (error instanceof Error && !error.message.includes('already been connected')) {
                    console.error('[AudioRecorder] Error setting up system audio visualization:', error);
                  } else {
                    console.log('[AudioRecorder] Audio source already connected (expected)');
                  }
                }
              }
            })
            .catch(err => {
              console.error('[AudioRecorder] Error auto-playing audio:', err);
              console.error('[AudioRecorder] Error details:', {
                name: err.name,
                message: err.message,
                stack: err.stack
              });
            });
        } else {
          console.log('[AudioRecorder] Audio not ready yet, waiting for canplay event. Current readyState:', systemAudio.readyState);
          // Wait for audio to be ready
            const handleCanPlay = () => {
            console.log('[AudioRecorder] canplay event fired, attempting to play...');
            applySystemPlaybackRate(systemAudio, systemPlaybackRate);
            systemAudio.play()
              .then(() => {
                console.log('[AudioRecorder] Audio play() succeeded after canplay');
                setIsSystemAudioPlaying(true);
                if (audioContextRef.current && systemAudio) {
                  try {
                    if (!systemAnalyserRef.current) {
                      const source = audioContextRef.current.createMediaElementSource(systemAudio);
                      const analyser = audioContextRef.current.createAnalyser();
                      analyser.fftSize = 256;
                      analyser.smoothingTimeConstant = 0.8;
                      systemAnalyserRef.current = analyser;
                      // Connect source -> analyser -> destination (important: must connect to destination!)
                      source.connect(analyser);
                      analyser.connect(audioContextRef.current.destination);
                    }
                    audioContextRef.current.resume();
                    visualizeSystemWaveform();
                  } catch (error) {
                    if (error instanceof Error && !error.message.includes('already been connected')) {
                      console.error('[AudioRecorder] Error setting up system audio visualization:', error);
                    }
                  }
                }
              })
              .catch(err => {
                console.error('[AudioRecorder] Error auto-playing audio after canplay:', err);
                console.error('[AudioRecorder] Error details:', {
                  name: err.name,
                  message: err.message,
                  stack: err.stack
                });
              });
            systemAudio.removeEventListener('canplay', handleCanPlay);
          };
          systemAudio.addEventListener('canplay', handleCanPlay);
          
          // Also try to load the audio if it hasn't started loading
          if (systemAudio.readyState === 0) {
            console.log('[AudioRecorder] Audio readyState is 0, calling load()');
            systemAudio.load();
          }
        }
      };

      // Try to play immediately or wait for ready
      playAudio();
    }, 100); // Small delay to ensure DOM is updated

    return () => {
      clearTimeout(timeoutId);
    };
  }, [autoPlay, systemAudioURL, disabled, systemPlaybackRate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        const elapsed = (Date.now() - recordingStartTimeRef.current) / 1000;
        setRecordingDuration(elapsed);
      }, 100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  // Handle external isRecording prop
  useEffect(() => {
    if (externalIsRecording && !isRecording && !disabled && !isRecordingRef.current) {
      // Double check MediaRecorder state
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      startRecording();
      }
    } else if (!externalIsRecording && isRecording) {
      stopRecording();
    }
  }, [externalIsRecording, disabled, isRecording]);

  useEffect(() => {
    if (!prewarmMic || disabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const tracks = streamRef.current?.getAudioTracks() ?? [];
        if (tracks.some((t) => t.readyState === 'live')) return;
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const alreadyLive = streamRef.current?.getAudioTracks().some((t) => t.readyState === 'live');
        if (alreadyLive) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (onRecordingStreamReady) {
          onRecordingStreamReady(stream);
        }
      } catch (e) {
        console.warn('[AudioRecorder] prewarmMic: microphone not available yet', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prewarmMic, disabled, onRecordingStreamReady]);

  useEffect(() => {
    if (micPrewarmRevokeTick === 0) return;
    if (isRecordingRef.current || mediaRecorderRef.current?.state === 'recording') return;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [micPrewarmRevokeTick]);

  // Handle autoPlayRecording - automatically play user recording after it's complete
  useEffect(() => {
    console.log('[AudioRecorder] autoPlayRecording useEffect triggered:', {
      autoPlayRecording,
      disabled,
      hasAudioBlob: !!audioBlob,
      hasAudioURL: !!audioURL,
      isPlaying
    });

    if (!autoPlayRecording || disabled || !audioBlob || !audioURL) {
      console.log('[AudioRecorder] autoPlayRecording conditions not met, returning early');
      return;
    }

    // Use a small delay to ensure audio element is ready
    const timeoutId = setTimeout(() => {
      const audio = audioRef.current;
      if (!audio) {
        console.warn('[AudioRecorder] Audio element not found when trying to auto-play recording');
        return;
      }

      console.log('[AudioRecorder] Audio element found for recording playback, readyState:', audio.readyState);

      // Check if audio is ready to play
      const playRecording = () => {
        if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or higher
          console.log('[AudioRecorder] Recording audio is ready, attempting to play...');
          audio.play()
            .then(() => {
              console.log('[AudioRecorder] Recording audio play() succeeded');
              setIsPlaying(true);
              if (audioContextRef.current && audio) {
                try {
                  // Setup visualization if not already done
                  if (!analyserRef.current) {
                    console.log('[AudioRecorder] Setting up recording audio visualization');
                    const source = audioContextRef.current.createMediaElementSource(audio);
                    const analyser = audioContextRef.current.createAnalyser();
                    analyser.fftSize = 8192; // Maximum FFT size for maximum waveform detail and sensitivity
                    analyser.smoothingTimeConstant = 0; // No smoothing for maximum responsiveness and sensitivity
                    analyserRef.current = analyser;
                    source.connect(analyser);
                    analyser.connect(audioContextRef.current.destination);
                  }
                  audioContextRef.current.resume();
                  visualizeWaveform();
                } catch (error) {
                  if (error instanceof Error && !error.message.includes('already been connected')) {
                    console.error('[AudioRecorder] Error setting up recording audio visualization:', error);
                  }
                }
              }
            })
            .catch(err => {
              console.error('[AudioRecorder] Error auto-playing recording audio:', err);
            });
        } else {
          console.log('[AudioRecorder] Recording audio not ready yet, waiting for canplay event');
          // Wait for audio to be ready
          const handleCanPlay = () => {
            console.log('[AudioRecorder] canplay event fired for recording, attempting to play...');
            audio.play()
              .then(() => {
                console.log('[AudioRecorder] Recording audio play() succeeded after canplay');
                setIsPlaying(true);
                if (audioContextRef.current && audio) {
                  try {
                    if (!analyserRef.current) {
                      const source = audioContextRef.current.createMediaElementSource(audio);
                      const analyser = audioContextRef.current.createAnalyser();
                      analyser.fftSize = 2048;
                      analyser.smoothingTimeConstant = 0.3;
                      analyserRef.current = analyser;
                      source.connect(analyser);
                      analyser.connect(audioContextRef.current.destination);
                    }
                    audioContextRef.current.resume();
                    visualizeWaveform();
                  } catch (error) {
                    if (error instanceof Error && !error.message.includes('already been connected')) {
                      console.error('[AudioRecorder] Error setting up recording audio visualization:', error);
                    }
                  }
                }
              })
              .catch(err => {
                console.error('[AudioRecorder] Error auto-playing recording audio after canplay:', err);
              });
            audio.removeEventListener('canplay', handleCanPlay);
          };
          audio.addEventListener('canplay', handleCanPlay);
          
          // Also try to load the audio if it hasn't started loading
          if (audio.readyState === 0) {
            console.log('[AudioRecorder] Recording audio readyState is 0, calling load()');
            audio.load();
          }
        }
      };

      // Try to play immediately or wait for ready
      playRecording();
    }, 100); // Small delay to ensure DOM is updated

    return () => {
      clearTimeout(timeoutId);
    };
  }, [autoPlayRecording, audioBlob, audioURL, disabled]);

  const generateStaticWaveform = (duration: number, setter: (data: number[]) => void) => {
    const data: number[] = [];
    const totalBars = 100;
    for (let i = 0; i < totalBars; i++) {
      const position = i / totalBars;
      const height = Math.sin(position * Math.PI * 4) * 0.3 + 0.5 + Math.random() * 0.2;
      data.push(Math.max(height, 0.2));
    }
    setter(data);
  };

  const startSystemAudio = () => {
    console.log('[AudioRecorder] startSystemAudio called:', {
      disabled,
      hasSystemAudioRef: !!systemAudioRef.current,
      isSystemAudioPlaying
    });
    
    if (disabled) {
      console.log('[AudioRecorder] startSystemAudio blocked: disabled');
      return;
    }
    
    if (!systemAudioRef.current) {
      console.warn('[AudioRecorder] startSystemAudio: No audio element found');
      return;
    }

    // Reset finished state when starting new playback
    setSystemAudioFinished(false);
    setSystemAudioCurrentTime(0);
    
    // Reset audio to beginning
    if (systemAudioRef.current) {
      systemAudioRef.current.currentTime = 0;
    }

    console.log('[AudioRecorder] Attempting to play audio...');
    applySystemPlaybackRate(systemAudioRef.current, systemPlaybackRate);
    systemAudioRef.current.play()
      .then(() => {
        console.log('[AudioRecorder] Audio play() succeeded in startSystemAudio');
    setIsSystemAudioPlaying(true);
      })
      .catch(err => {
        console.error('[AudioRecorder] Error playing system audio:', err);
      });
    
    if (audioContextRef.current && systemAudioRef.current) {
      try {
        // Check if source already exists to avoid error
        if (!systemAnalyserRef.current) {
          console.log('[AudioRecorder] Creating new audio source for visualization');
        const source = audioContextRef.current.createMediaElementSource(systemAudioRef.current);
        const analyser = audioContextRef.current.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        systemAnalyserRef.current = analyser;
          // Connect source -> analyser -> destination (important: must connect to destination!)
        source.connect(analyser);
          analyser.connect(audioContextRef.current.destination);
        } else {
          console.log('[AudioRecorder] Audio source already exists, reusing');
        }
        audioContextRef.current.resume();
        visualizeSystemWaveform();
      } catch (error) {
        if (error instanceof Error && error.message.includes('already been connected')) {
          console.log('[AudioRecorder] Audio source already connected (expected), reusing existing');
          // Try to reuse existing analyser if available
          if (systemAnalyserRef.current) {
            visualizeSystemWaveform();
          }
        } else {
          console.error('[AudioRecorder] Error setting up system audio visualization:', error);
        }
      }
    }
  };

  const visualizeSystemWaveform = () => {
    if (!systemAnalyserRef.current) return;

    const analyser = systemAnalyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateWaveform = () => {
      if (!isSystemAudioPlaying) {
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      
      const normalizedData: number[] = [];
      const step = Math.floor(bufferLength / 100);
      
      for (let i = 0; i < 100; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) {
          sum += dataArray[i * step + j] || 0;
        }
        const average = sum / step;
        normalizedData.push(average / 255);
      }

      setSystemWaveformData(normalizedData);

      if (isSystemAudioPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      }
    };

    updateWaveform();
  };

  const startRecording = async () => {
    try {
      // Prevent duplicate calls - if already recording, return early
      if (isRecording || isRecordingRef.current || (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording')) {
        console.warn('[AudioRecorder] startRecording: Already recording, skipping duplicate call');
        return;
      }
      
      // Reset previous recording state before starting new recording
      console.log('[AudioRecorder] startRecording: Resetting previous recording state...');
      
      // Stop and cleanup previous MediaRecorder if exists
      if (mediaRecorderRef.current) {
        const prevRecorder = mediaRecorderRef.current;
        if (prevRecorder.state !== 'inactive') {
          console.log('[AudioRecorder] startRecording: Stopping previous MediaRecorder');
          try {
            // Set ref to null first so onstop handler knows it's not current
            mediaRecorderRef.current = null;
            prevRecorder.stop();
          } catch (e) {
            console.warn('[AudioRecorder] startRecording: Error stopping previous MediaRecorder:', e);
            mediaRecorderRef.current = null;
          }
        } else {
          mediaRecorderRef.current = null;
        }
      }

      const micTracksLive =
        streamRef.current?.getAudioTracks().some((t) => t.readyState === 'live') ?? false;
      // After the cleanup block above, `mediaRecorderRef` is always cleared; reuse only depends on a live prewarm stream.
      const reuseMicStream = micTracksLive;

      if (!reuseMicStream) {
        if (streamRef.current) {
          console.log('[AudioRecorder] startRecording: Stopping previous stream tracks');
          streamRef.current.getTracks().forEach((track) => {
            track.stop();
            console.log('[AudioRecorder] startRecording: Stopped track', track.kind);
          });
          streamRef.current = null;
        }
      }

      audioChunksRef.current = [];

      if (audioRef.current) {
        const audio = audioRef.current;
        audio.pause();
        audio.src = '';
        audio.load();
        console.log('[AudioRecorder] startRecording: Audio element reset');
      }

      if (audioURLRef.current) {
        URL.revokeObjectURL(audioURLRef.current);
        audioURLRef.current = null;
        console.log('[AudioRecorder] startRecording: Previous blob URL revoked');
      }

      setAudioBlob(null);
      setAudioURL(null);
      setIsPlaying(false);
      setCurrentTime(0);

      let stream: MediaStream;
      if (reuseMicStream && streamRef.current) {
        console.log('[AudioRecorder] startRecording: Reusing prewarmed microphone stream');
        stream = streamRef.current;
        if (onRecordingStreamReady) {
          onRecordingStreamReady(stream);
        }
      } else {
        console.log('[AudioRecorder] startRecording: Requesting microphone access...');
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('[AudioRecorder] startRecording: Microphone access granted', {
          active: stream.active,
          tracks: stream.getTracks().length
        });
        streamRef.current = stream;
        if (onRecordingStreamReady) {
          onRecordingStreamReady(stream);
        }
      }

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('[AudioRecorder] startRecording: Created new AudioContext');
      }
      
      // Resume AudioContext if suspended (required by browser autoplay policy)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        console.log('[AudioRecorder] startRecording: AudioContext resumed');
      }

      if (micMediaStreamSourceRef.current) {
        try {
          micMediaStreamSourceRef.current.disconnect();
        } catch {
          /* ignore */
        }
        micMediaStreamSourceRef.current = null;
      }

      const source = audioContextRef.current.createMediaStreamSource(stream);
      micMediaStreamSourceRef.current = source;
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 8192; // Maximum FFT size for maximum waveform detail and sensitivity
      analyser.smoothingTimeConstant = 0; // No smoothing for maximum responsiveness and sensitivity
      analyserRef.current = analyser;
      
      // Connect source to analyser (for visualization)
      // Note: We don't connect to destination because we're recording, not playing back
      source.connect(analyser);

      console.log('[AudioRecorder] startRecording: AudioContext setup complete', {
        hasSource: !!source,
        hasAnalyser: !!analyser,
        fftSize: analyser.fftSize,
        audioContextState: audioContextRef.current.state
      });

      // Check available MIME types - prefer codec-specific types for better compatibility
      const mimeTypes = [
        'audio/webm;codecs=opus', // Best quality WebM
        'audio/webm', // Fallback WebM
        'audio/ogg;codecs=opus', // OGG with Opus
        'audio/ogg', // Fallback OGG
        'audio/mp4', // MP4 (less common for recording)
      ];
      let selectedMimeType = '';
      
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('[AudioRecorder] startRecording: Using MIME type:', mimeType);
          break;
        }
      }
      
      // If no specific type is supported, let browser choose
      if (!selectedMimeType) {
        console.warn('[AudioRecorder] startRecording: No preferred MIME type supported, browser will choose default');
        selectedMimeType = ''; // Let browser choose
      }

      // Ensure chunks array is empty before creating new MediaRecorder
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });
      mediaRecorderRef.current = mediaRecorder;
      
      console.log('[AudioRecorder] startRecording: MediaRecorder created', {
        state: mediaRecorder.state,
        mimeType: mediaRecorder.mimeType,
        chunksArrayLength: audioChunksRef.current.length
      });

      // Store mimeType in a ref to ensure it's available in onstop
      const mimeTypeRef = { current: selectedMimeType };

      // Track if we're waiting for final chunk
      let waitingForFinalChunk = false;
      let finalChunkTimeout: NodeJS.Timeout | null = null;
      let lastChunkTime = Date.now();
      let isStopped = false;
      const CHUNK_TIMEOUT = 500; // Wait 500ms after last chunk before processing (reduced for faster response)
      let chunkCountAtStop = 0;
      let blobCreated = false; // Track if blob has been created
      let dataAvailableAfterStop = false; // Track if we received data after stop

      mediaRecorder.ondataavailable = (event) => {
        // Check if this MediaRecorder is still the current one
        if (mediaRecorderRef.current !== mediaRecorder) {
          console.warn('[AudioRecorder] ondataavailable: Ignoring chunk from old MediaRecorder', {
            currentRecorder: !!mediaRecorderRef.current,
            thisRecorder: !!mediaRecorder,
            currentState: mediaRecorderRef.current?.state,
            thisState: mediaRecorder.state
          });
          return;
        }
        
        const currentChunkCount = audioChunksRef.current.length;
        const chunkSize = event.data.size;
        const chunkType = event.data.type;
        
        console.log('[AudioRecorder] ondataavailable:', {
          size: chunkSize,
          type: chunkType,
          chunksCount: currentChunkCount,
          waitingForFinal: waitingForFinalChunk,
          isStopped: isStopped,
          chunksSinceStop: isStopped ? currentChunkCount - chunkCountAtStop : 0,
          mediaRecorderState: mediaRecorder.state
        });
        
        // Ignore chunks if MediaRecorder is inactive and we're not waiting for final chunks
        if (mediaRecorder.state === 'inactive' && !isStopped && !waitingForFinalChunk) {
          console.warn('[AudioRecorder] ondataavailable: Ignoring chunk - MediaRecorder inactive and not waiting', {
            state: mediaRecorder.state,
            isStopped: isStopped,
            waitingForFinalChunk: waitingForFinalChunk
          });
          return;
        }
        
        if (chunkSize > 0) {
          audioChunksRef.current.push(event.data);
          
          // If we're stopped and receiving data, mark that we got data after stop
          if (isStopped) {
            dataAvailableAfterStop = true;
          }
          
          lastChunkTime = Date.now();
          
          console.log('[AudioRecorder] ondataavailable: Chunk added', {
            totalChunks: audioChunksRef.current.length,
            totalSize: audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0),
            isStopped: isStopped,
            blobCreated: blobCreated,
            dataAvailableAfterStop: dataAvailableAfterStop,
            mediaRecorderState: mediaRecorder.state
          });
          
          // If we're waiting for final chunks and MediaRecorder is stopped, reset timeout
          // This ensures we wait for the last chunk before processing
          if (waitingForFinalChunk && isStopped && !blobCreated) {
            // Reset timeout - wait for more chunks
            if (finalChunkTimeout) {
              clearTimeout(finalChunkTimeout);
            }
            finalChunkTimeout = setTimeout(() => {
              if (!blobCreated && mediaRecorder.state === 'inactive' && mediaRecorderRef.current === mediaRecorder) {
              const finalChunksCount = audioChunksRef.current.length;
                console.log('[AudioRecorder] ondataavailable: Processing blob after no new chunks', {
                finalChunksCount,
                chunksSinceStop: finalChunksCount - chunkCountAtStop,
                  totalSize: audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0),
                  dataAvailableAfterStop: dataAvailableAfterStop
              });
              waitingForFinalChunk = false;
              finalChunkTimeout = null;
              processBlob();
              }
            }, CHUNK_TIMEOUT);
          }
        } else {
          console.warn('[AudioRecorder] ondataavailable: Received empty chunk', {
            chunksCount: currentChunkCount,
            isStopped: isStopped,
            blobCreated: blobCreated,
            mediaRecorderState: mediaRecorder.state
          });
        }
      };

      const processBlob = () => {
        // Check if this MediaRecorder is still the current one
        if (mediaRecorderRef.current !== mediaRecorder) {
          console.warn('[AudioRecorder] processBlob: Ignoring - this is not the current MediaRecorder', {
            currentRecorder: !!mediaRecorderRef.current,
            thisRecorder: !!mediaRecorder,
            currentState: mediaRecorderRef.current?.state,
            thisState: mediaRecorder.state
          });
          return;
        }
        
        // Prevent multiple calls
        if (blobCreated) {
          console.log('[AudioRecorder] processBlob: Blob already created, skipping');
          return;
        }
        
        // Ensure MediaRecorder is inactive
        if (mediaRecorder.state !== 'inactive') {
          console.warn('[AudioRecorder] processBlob: MediaRecorder not inactive, waiting...', {
            state: mediaRecorder.state
          });
          setTimeout(() => {
            if (mediaRecorder.state === 'inactive' && !blobCreated && mediaRecorderRef.current === mediaRecorder) {
              processBlob();
            }
          }, 100);
          return;
        }
        
        console.log('[AudioRecorder] processBlob: Starting blob creation', {
          totalChunks: audioChunksRef.current.length,
          isStopped: isStopped,
          chunksSinceStop: isStopped ? audioChunksRef.current.length - chunkCountAtStop : 0,
          mediaRecorderState: mediaRecorder.state,
          dataAvailableAfterStop: dataAvailableAfterStop
        });
        
        // Revoke old URL if exists (use ref to get latest value)
        if (audioURLRef.current) {
          console.log('[AudioRecorder] processBlob: Revoking old blob URL');
          URL.revokeObjectURL(audioURLRef.current);
          audioURLRef.current = null;
        }
        
        // Use the actual mimeType from MediaRecorder if available
        const actualMimeType = mediaRecorder.mimeType || mimeTypeRef.current;
        console.log('[AudioRecorder] processBlob: MIME type from MediaRecorder', actualMimeType);
        
        // Filter out empty chunks and ensure we have chunks
        const validChunks = audioChunksRef.current.filter(chunk => chunk && chunk.size > 0);
        const totalSize = validChunks.reduce((sum, chunk) => sum + chunk.size, 0);
        
        console.log('[AudioRecorder] processBlob: Chunk analysis', {
          totalChunks: audioChunksRef.current.length,
          validChunks: validChunks.length,
          emptyChunks: audioChunksRef.current.length - validChunks.length,
          totalSize: totalSize,
          averageChunkSize: validChunks.length > 0 ? Math.round(totalSize / validChunks.length) : 0,
          firstChunkSize: validChunks.length > 0 ? validChunks[0].size : 0,
          lastChunkSize: validChunks.length > 0 ? validChunks[validChunks.length - 1].size : 0,
          mediaRecorderMimeType: mediaRecorder.mimeType
        });
        
        if (validChunks.length === 0) {
          console.error('[AudioRecorder] processBlob: No valid chunks! Cannot create blob.');
          setIsRecording(false);
          isRecordingRef.current = false;
          return;
        }
        
        // Validate that we have a reasonable amount of data
        if (totalSize < 1000) {
          console.warn('[AudioRecorder] processBlob: Very small blob size, may be incomplete', {
            totalSize: totalSize,
            chunks: validChunks.length
          });
        }
        
        // Use the actual MIME type from MediaRecorder - this is critical for compatibility
        // Don't try multiple MIME types - use the one MediaRecorder used
        let blob: Blob | null = null;
        let blobType = actualMimeType || 'audio/webm';
        
        // Create blob with the exact MIME type from MediaRecorder
        try {
          blob = new Blob(validChunks, { type: blobType });
          console.log('[AudioRecorder] processBlob: Blob created with MediaRecorder MIME type', {
            size: blob.size,
            type: blob.type,
            mimeType: blobType,
            expectedSize: totalSize
          });
        } catch (error) {
          console.warn('[AudioRecorder] processBlob: Failed with MediaRecorder MIME type, trying fallback...', error);
          // Fallback: try without explicit type
          try {
            blob = new Blob(validChunks);
            blobType = '';
            console.log('[AudioRecorder] processBlob: Blob created without type (fallback)', {
              size: blob.size,
              type: blob.type
            });
          } catch (error2) {
            console.error('[AudioRecorder] processBlob: Failed to create blob!', error2);
            setIsRecording(false);
            isRecordingRef.current = false;
            return;
          }
        }
        
        if (!blob) {
          console.error('[AudioRecorder] processBlob: Failed to create blob!');
          setIsRecording(false);
          isRecordingRef.current = false;
          return;
        }
        
        // Check if blob is valid
        if (blob.size === 0) {
          console.error('[AudioRecorder] processBlob: Blob is empty!');
          setIsRecording(false);
          isRecordingRef.current = false;
          return;
        }
        
        // Validate blob size matches chunk sizes
        const expectedSize = totalSize;
        if (blob.size !== expectedSize) {
          console.warn('[AudioRecorder] processBlob: Blob size mismatch!', {
            blobSize: blob.size,
            expectedSize: expectedSize,
            difference: blob.size - expectedSize
          });
        }
        
        console.log('[AudioRecorder] processBlob: Blob validation passed', {
          size: blob.size,
          type: blob.type,
          mimeType: blobType
        });
        
        setIsRecording(false);
        isRecordingRef.current = false;
        
        // Mark blob as created
        blobCreated = true;
        
        // Revoke previous URL if exists before creating new one
        // Use ref to get the latest value in closure
        if (audioURLRef.current) {
          console.log('[AudioRecorder] processBlob: Revoking previous blob URL before creating new one');
          URL.revokeObjectURL(audioURLRef.current);
          audioURLRef.current = null;
        }
        
        // Reset audio element before setting new blob
        if (audioRef.current) {
          const audio = audioRef.current;
          audio.pause();
          audio.src = '';
          audio.load();
          console.log('[AudioRecorder] processBlob: Audio element reset before setting new blob');
        }
        
        setAudioBlob(blob);
        
        // Create URL with the correct MIME type
        const url = URL.createObjectURL(blob);
        console.log('[AudioRecorder] processBlob: Blob URL created', {
          url: url.substring(0, 50) + '...',
          blobSize: blob.size,
          blobType: blob.type,
          mimeType: blobType,
          chunksUsed: validChunks.length,
          totalChunksAvailable: audioChunksRef.current.length,
          mediaRecorderMimeType: mediaRecorder.mimeType,
          firstChunkType: validChunks[0]?.type,
          lastChunkType: validChunks[validChunks.length - 1]?.type
        });
        // Update both state and ref
        audioURLRef.current = url;
        setAudioURL(url);
        
        // Wait a bit to ensure URL is set before calling callback
        // Increased delay to ensure blob is fully ready and all chunks are processed
        setTimeout(() => {
          if (onRecordingComplete) {
            console.log('[AudioRecorder] processBlob: Calling onRecordingComplete', {
              blobSize: blob!.size,
              blobType: blob!.type,
              mimeType: blobType,
              chunksUsed: validChunks.length,
              duration: recordingDuration
            });
            onRecordingComplete(blob!, recordingDuration);
          }
        }, 300); // Increased delay to ensure blob is fully ready and audio element is reset
      };

      mediaRecorder.onstop = () => {
        // Check if this MediaRecorder is still the current one
        if (mediaRecorderRef.current !== mediaRecorder) {
          console.warn('[AudioRecorder] onstop: Ignoring - this is not the current MediaRecorder', {
            currentRecorder: !!mediaRecorderRef.current,
            thisRecorder: !!mediaRecorder,
            currentState: mediaRecorderRef.current?.state,
            thisState: mediaRecorder.state
          });
          return;
        }
        
        // Set isStopped flag FIRST before any other operations
        isStopped = true;
        chunkCountAtStop = audioChunksRef.current.length;
        blobCreated = false; // Reset blob created flag
        dataAvailableAfterStop = false; // Reset flag
        
        console.log('[AudioRecorder] onstop: MediaRecorder stopped', {
          chunksCount: chunkCountAtStop,
          totalSize: audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0),
          mimeType: mimeTypeRef.current,
          mediaRecorderState: mediaRecorder.state,
          mediaRecorderMimeType: mediaRecorder.mimeType,
          isStopped: isStopped
        });
        
        // Request final data chunk explicitly - this is critical!
        try {
          // Request data multiple times to ensure we get all buffered data
          mediaRecorder.requestData();
          console.log('[AudioRecorder] onstop: Requested final data chunk');
          
          // Request again after a short delay to catch any remaining data
          setTimeout(() => {
            try {
              if (mediaRecorder.state === 'inactive') {
                // Even though it's inactive, try to request data one more time
                console.log('[AudioRecorder] onstop: Requesting data again after delay');
              }
            } catch (e) {
              // Ignore errors on second request
            }
          }, 100);
        } catch (e) {
          console.warn('[AudioRecorder] onstop: Could not request final data:', e);
        }
        
        // Wait for all chunks to arrive (including those that come after onstop)
        // MediaRecorder may send chunks asynchronously after stop()
        waitingForFinalChunk = true;
        lastChunkTime = Date.now();
        
        console.log('[AudioRecorder] onstop: Waiting for final chunks', {
          timeout: CHUNK_TIMEOUT + 'ms',
          currentChunks: chunkCountAtStop,
          isStopped: isStopped
        });
        
        // Clear any existing timeout first
        if (finalChunkTimeout) {
          clearTimeout(finalChunkTimeout);
        }
        
        // Use a longer timeout to ensure all chunks arrive
        // But also check if we got data after stop
        finalChunkTimeout = setTimeout(() => {
          // Check if this MediaRecorder is still the current one
          if (mediaRecorderRef.current !== mediaRecorder) {
            console.warn('[AudioRecorder] onstop: Timeout ignored - this is not the current MediaRecorder');
            return;
          }
          
          // Only process if blob hasn't been created yet and MediaRecorder is inactive
          if (!blobCreated && mediaRecorder.state === 'inactive') {
          const finalChunksCount = audioChunksRef.current.length;
          const chunksReceivedAfterStop = finalChunksCount - chunkCountAtStop;
          const finalTotalSize = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);
          
          console.log('[AudioRecorder] onstop: Processing blob after timeout', {
            chunksAtStop: chunkCountAtStop,
            finalChunksCount,
            chunksReceivedAfterStop,
            totalSize: finalTotalSize,
            isStopped: isStopped,
              blobCreated: blobCreated,
              mediaRecorderState: mediaRecorder.state,
              dataAvailableAfterStop: dataAvailableAfterStop
          });
          
          waitingForFinalChunk = false;
          finalChunkTimeout = null;
            processBlob();
          } else {
            if (blobCreated) {
              console.log('[AudioRecorder] onstop: Blob already created, skipping');
            } else {
              console.warn('[AudioRecorder] onstop: MediaRecorder not inactive yet, waiting more...', {
                state: mediaRecorder.state
              });
              // Wait a bit more
              setTimeout(() => {
                if (!blobCreated && mediaRecorder.state === 'inactive' && mediaRecorderRef.current === mediaRecorder) {
                  processBlob();
                }
              }, 200);
            }
          }
        }, CHUNK_TIMEOUT);

        // Stop tracks after processing blob
        setTimeout(() => {
          stream.getTracks().forEach(track => {
            track.stop();
            console.log('[AudioRecorder] onstop: Stopped track', track.kind);
          });
        }, CHUNK_TIMEOUT + 500);
      };

      mediaRecorder.onerror = (event) => {
        console.error('[AudioRecorder] MediaRecorder error:', event);
      };

      mediaRecorder.onstart = () => {
        console.log('[AudioRecorder] MediaRecorder started', {
          state: mediaRecorder.state,
          mimeType: mediaRecorder.mimeType
        });
      };

      console.log('[AudioRecorder] startRecording: Starting MediaRecorder...');
      
      // Set recording state first
      setIsRecording(true);
      isRecordingRef.current = true;
      recordingStartTimeRef.current = Date.now();
      setRecordingDuration(0);

      // Start MediaRecorder with timeslice for real-time data
      // This ensures we get chunks regularly during recording
      // Using a larger timeslice (1000ms) to get more complete chunks
      // This helps ensure each chunk is more complete and reduces fragmentation
      mediaRecorder.start(1000); // Request data every 1000ms (1 second) for more complete chunks

      // Use setTimeout to ensure state is updated before calling visualizeWaveform
      setTimeout(() => {
        console.log('[AudioRecorder] startRecording: Starting waveform visualization', {
          isRecording: true, // We know it's true because we just set it
          hasAnalyser: !!analyserRef.current,
          mediaRecorderState: mediaRecorderRef.current?.state
        });
      visualizeWaveform();
      }, 100);
    } catch (error) {
      console.error('[AudioRecorder] Error accessing microphone:', error);
      alert('Microphone access denied. Please allow microphone access to record audio.');
    }
  };

  const stopRecording = () => {
    console.log('[AudioRecorder] stopRecording called', {
      hasMediaRecorder: !!mediaRecorderRef.current,
      isRecording,
      mediaRecorderState: mediaRecorderRef.current?.state
    });
    
    if (mediaRecorderRef.current) {
      const state = mediaRecorderRef.current.state;
      if (state === 'recording' || state === 'paused') {
        console.log('[AudioRecorder] stopRecording: Stopping MediaRecorder, current state:', state);
        
        // Request final data before stopping
        try {
          mediaRecorderRef.current.requestData();
        } catch (e) {
          console.warn('[AudioRecorder] stopRecording: Could not request final data:', e);
        }
        
        // Stop MediaRecorder - this will trigger onstop
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        isRecordingRef.current = false;
        if (onRecordingStopped) {
          onRecordingStopped();
        }
        
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        
        // Don't stop tracks here - let onstop handle it after a delay
      } else {
        console.warn('[AudioRecorder] stopRecording: MediaRecorder is not in recording state:', state);
        setIsRecording(false);
        isRecordingRef.current = false;
      }
    } else {
      console.warn('[AudioRecorder] stopRecording: No MediaRecorder instance');
      setIsRecording(false);
      isRecordingRef.current = false;
    }
  };

  const visualizeWaveform = () => {
    if (!analyserRef.current) {
      console.log('[AudioRecorder] visualizeWaveform: No analyser found');
      return;
    }

    console.log('[AudioRecorder] visualizeWaveform: Starting waveform visualization', {
      isRecording,
      isPlaying,
      hasAnalyser: !!analyserRef.current,
      mediaRecorderState: mediaRecorderRef.current?.state
    });

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateWaveform = () => {
      // Check both React state, ref, and MediaRecorder state to avoid stale closure issues
      const currentlyRecording = isRecordingRef.current || isRecording || (mediaRecorderRef.current?.state === 'recording');
      const currentlyPlaying = isPlaying;
      
      if (!currentlyRecording && !currentlyPlaying) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      if (!analyserRef.current) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      // Use getByteTimeDomainData for better waveform visualization (shows actual audio waveform)
      analyser.getByteTimeDomainData(dataArray);
      
      const normalizedData: number[] = [];
      const step = Math.floor(bufferLength / 100);
      
      for (let i = 0; i < 100; i++) {
        let max = 0;
        for (let j = 0; j < step; j++) {
          const index = i * step + j;
          if (index < bufferLength) {
            // Time domain data is 0-255, center is 128
            const value = Math.abs(dataArray[index] - 128);
            max = Math.max(max, value);
          }
        }
        // Normalize to 0-1 range (0-128 becomes 0-1), but scale up for better visibility
        const normalized = Math.min(max / 128, 1);
        // Scale up significantly for better visibility when recording (4x for maximum sensitivity)
        // Also add a minimum threshold boost to catch quieter sounds
        const boosted = currentlyRecording 
          ? Math.min(normalized * 4 + (normalized > 0 ? 0.1 : 0), 1) 
          : normalized;
        normalizedData.push(boosted);
      }

      setWaveformData(normalizedData);

      // Continue animation if still recording or playing
      if (currentlyRecording || currentlyPlaying) {
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      } else {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }
    };

    // Cancel any existing animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    updateWaveform();
  };

  const handlePlay = () => {
    console.log('[AudioRecorder] handlePlay called', {
      hasAudioURL: !!audioURL,
      hasAudioRef: !!audioRef.current,
      isPlaying,
      hasAudioBlob: !!audioBlob,
      audioURL: audioURL ? audioURL.substring(0, 50) + '...' : null
    });

    if (!audioBlob) {
      console.warn('[AudioRecorder] handlePlay: No audio blob');
      return;
    }

    // Ensure audio element exists and has correct src
    if (!audioRef.current) {
      console.warn('[AudioRecorder] handlePlay: No audio ref');
      return;
    }

    // Always ensure src is set correctly
    if (audioURL) {
      const currentSrc = audioRef.current.src;
      const blobURL = audioURL;
      
      // Check if src needs to be updated
      if (!currentSrc || currentSrc !== blobURL) {
        console.log('[AudioRecorder] handlePlay: Updating audio src', {
          oldSrc: currentSrc ? currentSrc.substring(0, 50) + '...' : 'none',
          newSrc: blobURL.substring(0, 50) + '...'
        });
        audioRef.current.src = blobURL;
        audioRef.current.load(); // Reload the audio element
        
        // Wait for load to complete
        const handleLoadStart = () => {
          console.log('[AudioRecorder] handlePlay: Audio load started');
        };
        const handleLoadedData = () => {
          console.log('[AudioRecorder] handlePlay: Audio loaded data, readyState:', audioRef.current?.readyState);
          audioRef.current?.removeEventListener('loadstart', handleLoadStart);
          audioRef.current?.removeEventListener('loadeddata', handleLoadedData);
        };
        
        audioRef.current.addEventListener('loadstart', handleLoadStart);
        audioRef.current.addEventListener('loadeddata', handleLoadedData);
      } else {
        console.log('[AudioRecorder] handlePlay: Audio src already set correctly');
      }
    }

    if (isPlaying) {
      console.log('[AudioRecorder] handlePlay: Pausing playback');
      audioRef.current.pause();
      setIsPlaying(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    } else {
      console.log('[AudioRecorder] handlePlay: Starting playback');
      // Wait for audio to be ready
      const playAudio = () => {
        if (audioRef.current && audioRef.current.readyState >= 2) {
          audioRef.current.play()
            .then(() => {
              console.log('[AudioRecorder] handlePlay: Playback started successfully');
      setIsPlaying(true);
      
      if (audioRef.current && audioContextRef.current) {
        try {
                  // Check if analyser already exists (might be from recording)
                  if (!analyserRef.current) {
                    console.log('[AudioRecorder] handlePlay: Setting up audio visualization');
          const source = audioContextRef.current.createMediaElementSource(audioRef.current);
          const analyser = audioContextRef.current.createAnalyser();
                    analyser.fftSize = 8192; // Maximum FFT size for maximum waveform detail and sensitivity
                    analyser.smoothingTimeConstant = 0; // No smoothing for maximum responsiveness and sensitivity
                    analyserRef.current = analyser;
                    source.connect(analyser);
                    analyser.connect(audioContextRef.current.destination);
                  }
                  audioContextRef.current.resume();
                  visualizeWaveform();
                } catch (error) {
                  console.error('[AudioRecorder] handlePlay: Error setting up visualization', error);
                }
              }
            })
            .catch(err => {
              console.error('[AudioRecorder] handlePlay: Error playing audio', err);
              console.error('[AudioRecorder] handlePlay: Error details', {
                name: err.name,
                message: err.message,
                readyState: audioRef.current?.readyState,
                src: audioRef.current?.src
              });
            });
        } else {
          console.log('[AudioRecorder] handlePlay: Audio not ready, waiting for canplay. Current readyState:', audioRef.current?.readyState);
          
          if (!audioRef.current) {
            console.warn('[AudioRecorder] handlePlay: Audio ref lost while waiting for canplay');
            return;
          }
          
          // Set up multiple event listeners to catch when audio is ready
          const audio = audioRef.current;
          let playAttempted = false;
          
          const attemptPlay = () => {
            if (playAttempted) return;
            playAttempted = true;
            
            console.log('[AudioRecorder] handlePlay: Attempting to play after event, readyState:', audio.readyState);
            
            audio.play()
              .then(() => {
                console.log('[AudioRecorder] handlePlay: Playback started after event');
                setIsPlaying(true);
                if (audio && audioContextRef.current) {
                  try {
                    if (!analyserRef.current) {
                      const source = audioContextRef.current.createMediaElementSource(audio);
                      const analyser = audioContextRef.current.createAnalyser();
                      analyser.fftSize = 2048;
                      analyser.smoothingTimeConstant = 0.3;
                      analyserRef.current = analyser;
                      source.connect(analyser);
                      analyser.connect(audioContextRef.current.destination);
                    }
                    audioContextRef.current.resume();
                    visualizeWaveform();
                  } catch (error) {
                    console.error('[AudioRecorder] handlePlay: Error setting up visualization', error);
                  }
                }
              })
              .catch(err => {
                console.error('[AudioRecorder] handlePlay: Error playing audio after event', err);
                playAttempted = false; // Allow retry
              });
          };
          
          // Try multiple events
          const handleCanPlay = () => {
            console.log('[AudioRecorder] handlePlay: canplay event fired');
            attemptPlay();
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
          };
          
          const handleCanPlayThrough = () => {
            console.log('[AudioRecorder] handlePlay: canplaythrough event fired');
            attemptPlay();
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
          };
          
          const handleLoadedData = () => {
            console.log('[AudioRecorder] handlePlay: loadeddata event fired, readyState:', audio.readyState);
            if (audio.readyState >= 2) {
              attemptPlay();
              audio.removeEventListener('canplay', handleCanPlay);
              audio.removeEventListener('canplaythrough', handleCanPlayThrough);
              audio.removeEventListener('loadeddata', handleLoadedData);
            }
          };
          
          audio.addEventListener('canplay', handleCanPlay);
          audio.addEventListener('canplaythrough', handleCanPlayThrough);
          audio.addEventListener('loadeddata', handleLoadedData);
          
          // Fallback: try to play after a timeout if events don't fire
          setTimeout(() => {
            if (!playAttempted && audioRef.current && audioRef.current.readyState >= 2) {
              console.log('[AudioRecorder] handlePlay: Fallback - attempting to play after timeout');
              attemptPlay();
            }
          }, 1000);
        }
      };

      playAudio();
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (systemAudioRef.current) {
      systemAudioRef.current.pause();
      systemAudioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsSystemAudioPlaying(false);
    setCurrentTime(0);
    setSystemAudioCurrentTime(0);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const handleFinishAttempt = () => {
    if (isRecording) {
      stopRecording();
    }
    if (onFinishAttempt) {
      onFinishAttempt();
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      // Only set duration if it's valid (not Infinity or NaN)
      if (audio.duration && isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Call onPlaybackComplete if provided (when user recording playback ends)
      if (onPlaybackComplete && audioBlob) {
        console.log('[AudioRecorder] User recording playback ended, calling onPlaybackComplete');
        onPlaybackComplete();
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', () => {
      // Only set duration if it's valid (not Infinity or NaN)
      if (audio.duration && isFinite(audio.duration) && !isNaN(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    });
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', () => {});
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioURL, audioBlob, onPlaybackComplete]);

  const formatTime = (seconds: number) => {
    // Handle invalid values (Infinity, NaN, etc.)
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const systemProgress = systemAudioDuration > 0 ? (systemAudioCurrentTime / systemAudioDuration) * 100 : 0;
  const recordingProgress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-recorder-container">
      {/* System Audio Section (Top) */}
      <div className="audio-section system-audio-section">
        <div className="audio-section-header">
          <label className="audio-section-label">Playback</label>
          <SystemPlaybackSpeedControl
            value={systemPlaybackRate}
            onChange={setSystemPlaybackRate}
            disabled={disabled}
          />
        </div>
        <div className="audio-waveform-wrapper">
          {/* Playhead Line */}
          <div className="playhead-line-vertical"></div>

          {/* Waveform */}
          <div className="waveform-container">
            <div className="waveform-visualization">
              {systemWaveformData.length > 0 ? (
                systemWaveformData.map((height, i) => {
                  const position = (i / 100) * 100;
                  const isActive = position <= systemProgress && isSystemAudioPlaying;
                  return (
                    <div
                      key={i}
                      className={`waveform-bar system-bar ${isActive ? 'active' : ''}`}
                      style={{ height: `${Math.max(height * 100, 20)}%` }}
                    />
                  );
                })
              ) : (
                Array.from({ length: 100 }).map((_, i) => (
                  <div
                    key={i}
                    className="waveform-bar system-bar"
                    style={{ height: '20%' }}
                  />
                ))
              )}
            </div>
            {/* Playhead */}
            {isSystemAudioPlaying && (
              <div 
                className="playhead-line" 
                style={{ left: `${systemProgress}%` }}
              >
                <div className="playhead-circle"></div>
              </div>
            )}
            {/* Time Display */}
            <div className="time-display">
              <span className="current-time">{formatTime(systemAudioCurrentTime)}</span>
              <span className="duration">{formatTime(systemAudioDuration || 14)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* User Recording Section (Bottom) */}
      <div className="audio-section user-recording-section">
        <div className="audio-section-header">
          <label className="audio-section-label">Your recording</label>
        </div>
        <div className="audio-waveform-wrapper">
          {/* Playhead Line */}
          <div className="playhead-line-vertical"></div>

          {/* Waveform */}
          <div className="waveform-container">
            <div className="waveform-visualization recording-waveform">
              {waveformData.length > 0 ? (
                waveformData.map((height, i) => {
                  const position = (i / 100) * 100;
                  const isActive = (position <= recordingProgress && audioBlob) || (isRecording && i < (recordingDuration / (recordingDuration || 14)) * 100);
                  // Ensure minimum height for visibility, scale up the waveform for better visibility
                  const barHeight = Math.max(height * 120, isRecording ? 15 : 10); // Scale up by 120% and ensure minimum 15px when recording
                  return (
                    <div
                      key={i}
                      className={`waveform-bar recording-bar ${isActive ? 'active' : ''} ${isRecording ? 'recording' : ''}`}
                      style={{ 
                        height: `${barHeight}%`,
                        minHeight: isRecording ? '15px' : '10px'
                      }}
                    />
                  );
                })
              ) : (
                Array.from({ length: 100 }).map((_, i) => (
                  <div
                    key={i}
                    className={`waveform-bar recording-bar ${isRecording ? 'recording' : ''}`}
                    style={{ height: isRecording ? '15px' : '10px' }}
                  />
                ))
              )}
            </div>
            {/* Background for recording segment */}
            {(isRecording || audioBlob) && (
              <div className="recording-segment-bg"></div>
            )}
            {/* Playhead */}
            {(isPlaying || audioBlob) && (
              <div 
                className="playhead-line recording-playhead" 
                style={{ left: `${recordingProgress}%` }}
              >
                <div className="playhead-circle"></div>
              </div>
            )}
            {/* Time Display */}
            <div className="time-display">
              <span className="current-time">{formatTime(currentTime || recordingDuration)}</span>
              {(isFinite(duration) && !isNaN(duration) && duration > 0) ? (
                <span className="duration">{formatTime(duration)}</span>
              ) : recordingDuration > 0 ? (
                <span className="duration">{formatTime(recordingDuration)}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="recorder-controls-bottom">
        <div className={`attempts-display ${externalAttempts > 2 ? 'attempts-warning' : ''}`}>
          Attempts: {externalAttempts}
          {externalAttempts > 2 && (
            <span className="warning-icon" title="More than 2 attempts">
              {React.createElement(FiAlertTriangle as React.ComponentType<{ size?: number }>, { size: 16 })}
            </span>
          )}
        </div>

        {/* Show Start button only when system audio hasn't finished or is playing */}
        {!systemAudioFinished && !isRecording && (
          <div className="recorder-nav-actions">
            {showPrevious && (
              <button
                type="button"
                className="previous-clip-btn"
                onClick={onPrevious}
                disabled={disabled || !canGoPrevious}
              >
                Previous
              </button>
            )}
            <button
              className="start-btn-recorder recorder-cta-wide"
              onClick={() => {
                if (onStart) {
                  onStart();
                }
                startSystemAudio();
              }}
              disabled={disabled || isSystemAudioPlaying || isRecording}
            >
              Play
            </button>
          </div>
        )}

        {/* Show Repeat and Start Recording buttons when system audio finished but recording hasn't started */}
        {systemAudioFinished && !isRecording && (
          <div className="recorder-post-system-actions">
            {showPrevious && (
              <button
                type="button"
                className="previous-clip-btn"
                onClick={onPrevious}
                disabled={disabled || !canGoPrevious}
              >
                Previous
              </button>
            )}
            <button
              className="start-btn-recorder"
              onClick={onRepeatSystemAudio || startSystemAudio}
              disabled={disabled || isSystemAudioPlaying}
              type="button"
            >
              Repeat
            </button>
            <button
              className="start-btn-recorder"
              onClick={onStartRecording}
              disabled={disabled}
              type="button"
            >
              Start Recording
            </button>
          </div>
        )}

        {/* Show Previous + Next when recording */}
        {isRecording && (
          <div className="recorder-nav-actions">
            {showPrevious && (
              <button
                type="button"
                className="previous-clip-btn"
                onClick={onPrevious}
                disabled={disabled || !canGoPrevious}
              >
                Previous
              </button>
            )}
            <button
              className="finish-attempt-btn"
              onClick={handleFinishAttempt}
              disabled={disabled}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Hidden Audio Elements */}
      {systemAudioURL && (
        <audio
          ref={systemAudioRef}
          src={systemAudioURL}
          onPlay={() => {
            console.log('[AudioRecorder] Audio onPlay event fired');
            setIsSystemAudioPlaying(true);
            if (systemAudioRef.current) {
              systemAudioRef.current.volume = 1.0;
              applySystemPlaybackRate(systemAudioRef.current, systemPlaybackRate);
            }
          }}
          onPause={() => {
            console.log('[AudioRecorder] Audio onPause event fired - checking why...', {
              paused: systemAudioRef.current?.paused,
              ended: systemAudioRef.current?.ended,
              currentTime: systemAudioRef.current?.currentTime,
              duration: systemAudioRef.current?.duration,
              volume: systemAudioRef.current?.volume
            });
            // Only set to false if audio actually paused (not ended)
            if (systemAudioRef.current && !systemAudioRef.current.ended) {
              setIsSystemAudioPlaying(false);
            }
          }}
          onLoadedMetadata={() => {
            console.log('[AudioRecorder] Audio onLoadedMetadata event fired, duration:', systemAudioRef.current?.duration);
            if (systemAudioRef.current) {
              systemAudioRef.current.volume = 1.0;
              applySystemPlaybackRate(systemAudioRef.current, systemPlaybackRate);
              console.log('[AudioRecorder] Audio volume set to 1.0');
            }
          }}
          onCanPlay={() => {
            console.log('[AudioRecorder] Audio onCanPlay event fired');
            if (systemAudioRef.current) {
              systemAudioRef.current.volume = 1.0;
              applySystemPlaybackRate(systemAudioRef.current, systemPlaybackRate);
            }
          }}
          onError={(e) => {
            console.error('[AudioRecorder] Audio error event:', e);
            console.error('[AudioRecorder] Audio error details:', {
              error: systemAudioRef.current?.error,
              code: systemAudioRef.current?.error?.code,
              message: systemAudioRef.current?.error?.message
            });
          }}
          preload="auto"
        />
      )}
      {audioURL && (
        <audio
          ref={audioRef}
          src={audioURL}
          onPlay={() => {
            const audio = audioRef.current;
            console.log('[AudioRecorder] User recording audio onPlay event fired', {
              src: audio?.src,
              readyState: audio?.readyState,
              duration: audio?.duration,
              currentTime: audio?.currentTime,
              blobSize: audioBlob?.size,
              blobType: audioBlob?.type
            });
            setIsPlaying(true);
          }}
          onPause={() => {
            const audio = audioRef.current;
            console.log('[AudioRecorder] User recording audio onPause event fired', {
              paused: audio?.paused,
              ended: audio?.ended,
              currentTime: audio?.currentTime
            });
            setIsPlaying(false);
          }}
          onLoadedMetadata={() => {
            const audio = audioRef.current;
            console.log('[AudioRecorder] User recording audio onLoadedMetadata event fired', {
              duration: audio?.duration,
              readyState: audio?.readyState,
              readyStateText: audio ? ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][audio.readyState] : 'N/A',
              src: audio?.src,
              blobSize: audioBlob?.size,
              blobType: audioBlob?.type
            });
            if (audio) {
              audio.volume = 1.0;
            }
          }}
          onCanPlay={() => {
            const audio = audioRef.current;
            console.log('[AudioRecorder] User recording audio onCanPlay event fired', {
              readyState: audio?.readyState,
              duration: audio?.duration,
              src: audio?.src
            });
          }}
          onLoadStart={() => {
            console.log('[AudioRecorder] User recording audio loadstart event fired', {
              src: audioRef.current?.src,
              blobSize: audioBlob?.size,
              blobType: audioBlob?.type
            });
          }}
          onLoadedData={() => {
            const audio = audioRef.current;
            console.log('[AudioRecorder] User recording audio loadeddata event fired', {
              readyState: audio?.readyState,
              duration: audio?.duration,
              blobSize: audioBlob?.size
            });
          }}
          onError={(e) => {
            const audio = audioRef.current;
            console.error('[AudioRecorder] User recording audio error event:', e);
            console.error('[AudioRecorder] User recording audio error details:', {
              error: audio?.error,
              code: audio?.error?.code,
              message: audio?.error?.message,
              src: audio?.src,
              blobSize: audioBlob?.size,
              blobType: audioBlob?.type,
              readyState: audio?.readyState,
              networkState: audio?.networkState
            });
            
            // Try to diagnose the issue
            if (audio?.error) {
              const errorCode = audio.error.code;
              const errorMessages: { [key: number]: string } = {
                1: 'MEDIA_ERR_ABORTED - The user aborted the loading',
                2: 'MEDIA_ERR_NETWORK - A network error occurred',
                3: 'MEDIA_ERR_DECODE - An error occurred while decoding',
                4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - The media resource is not supported'
              };
              console.error('[AudioRecorder] Error code meaning:', errorMessages[errorCode] || 'Unknown error code');
              
              // If blob is not supported (code 4), try to fix by recreating with correct MIME type
              if (errorCode === 4 && audioBlob && audioURL) {
                console.warn('[AudioRecorder] Blob not supported, attempting to fix by recreating with different approach...');
                
                // Revoke current URL
                URL.revokeObjectURL(audioURL);
                
                // Try different approaches to fix the blob
                const fixAttempts = [
                  // Try with the actual MIME type from MediaRecorder if available
                  () => {
                    if (mediaRecorderRef.current?.mimeType) {
                      const fixedBlob = new Blob([audioBlob], { type: mediaRecorderRef.current.mimeType });
                      return URL.createObjectURL(fixedBlob);
                    }
                    return null;
                  },
                  // Try with explicit codec
                  () => {
                    const fixedBlob = new Blob([audioBlob], { type: 'audio/webm;codecs=opus' });
                    return URL.createObjectURL(fixedBlob);
                  },
                  // Try with simple webm
                  () => {
                    const fixedBlob = new Blob([audioBlob], { type: 'audio/webm' });
                    return URL.createObjectURL(fixedBlob);
                  },
                  // Last resort: try without type
                  () => {
                    const fixedBlob = new Blob([audioBlob]);
                    return URL.createObjectURL(fixedBlob);
                  }
                ];
                
                let fixed = false;
                for (const attempt of fixAttempts) {
                  try {
                    const newURL = attempt();
                    if (newURL && audioRef.current) {
                      console.log('[AudioRecorder] Attempting to fix blob with new URL:', newURL.substring(0, 50));
                      // Revoke old URL if exists
                      if (audioURLRef.current) {
                        URL.revokeObjectURL(audioURLRef.current);
                      }
                      audioURLRef.current = newURL;
                      setAudioURL(newURL);
                      audioRef.current.src = newURL;
                      audioRef.current.load();
                      fixed = true;
                      break;
                    }
                  } catch (err) {
                    console.warn('[AudioRecorder] Fix attempt failed:', err);
                    continue;
                  }
                }
                
                if (!fixed) {
                  console.error('[AudioRecorder] Could not fix blob, playback may not work');
                }
              }
            }
          }}
          preload="auto"
        />
      )}
    </div>
  );
};

export default AudioRecorder;
